package org.example.project.screens_cus

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.enginetabular.TabularScreen
import org.example.project.service.GlobalManager
import org.example.project.service.YardApi
import org.example.project.utils.All

private fun kontrollToggleValue(value: String): Boolean =
    when (value.trim().lowercase()) {
        "\u2705", "\u00E2\u0153\u2026", "true", "1", "yes", "ja", "aktiv" -> true
        else -> false
    }

private fun kontrollToggleText(value: Boolean): String =
    if (value) "\u2705" else "\u2B1C"

private fun All.normalizedKontrolle(): All {
    val resolvedKontrollId = kontrollId.ifBlank { id }
    val resolvedPrio = abfragePrio.takeIf { it > 0 } ?: resolvedKontrollId.toIntOrNull() ?: 999
    return copy(
        id = resolvedKontrollId,
        kontrollId = resolvedKontrollId,
        abfragePrio = resolvedPrio
    )
}

private fun sortKontrollen(items: List<All>): List<All> =
    items
        .distinctBy { it.kontrollId.ifBlank { it.id } }
        .map { it.normalizedKontrolle() }
        .sortedWith(
            compareBy<All> { it.abfragePrio.takeIf { prio -> prio > 0 } ?: Int.MAX_VALUE }
                .thenBy { it.kontrollId.toIntOrNull() ?: Int.MAX_VALUE }
                .thenBy { it.kontrollId }
        )

private fun mergeKontrollDefaults(loaded: List<All>): Pair<List<All>, List<All>> {
    val normalizedLoaded = sortKontrollen(loaded)
    if (normalizedLoaded.isNotEmpty()) {
        return normalizedLoaded to emptyList()
    }
    val loadedById = normalizedLoaded.associateBy { it.kontrollId }
    val missingDefaults = standardKontrollen
        .map { it.normalizedKontrolle() }
        .filter { it.kontrollId !in loadedById }

    return sortKontrollen(normalizedLoaded + missingDefaults) to missingDefaults
}

@Composable
fun CUSKontrollverwaltung(art: String = org.example.project.service.currentSaveArt()) {
    var items by remember { mutableStateOf<List<All>>(emptyList()) }
    val scope = rememberCoroutineScope()

    suspend fun reloadKontrollen() {
        val loaded = YardApi.loadKontrolleDefinitionen()
        val (merged, missingDefaults) = mergeKontrollDefaults(loaded)
        items = merged

        if (missingDefaults.isNotEmpty()) {
            GlobalManager.markLocalScreenWrite("KontrollVerwaltung")
            YardApi.saveKontrolleDefinitionen(missingDefaults)
            items = sortKontrollen(YardApi.loadKontrolleDefinitionen())
        }
    }

    LaunchedEffect(art) {
        reloadKontrollen()
    }

    val headers = listOf("ID", "Prio", "Bezeichnung", "Anzeige", "WA", "WE", "Pflicht", "Typ", "Optionen")

    TabularScreen(
        screenKey = "KontrollVerwaltung",
        rollenKey = "KontrollVerwaltung",
        title = "Kontrollpunkte Verwaltung",
        headers = headers,
        models = items,
        data = items.map { def ->
            def.kontrollId to listOf(
                def.kontrollId,
                def.abfragePrio.takeIf { it > 0 }?.toString() ?: "",
                def.bezeichnung,
                def.kontrollBezeichnung,
                if (def.aktivWa) "✅" else "⬜",
                if (def.aktivWe) "✅" else "⬜",
                if (def.kontrollPflicht) "✅" else "⬜",
                def.kontrollTyp,
                def.kontrollOptions.joinToString(", ")
            ).mapIndexed { index, value ->
                when (index) {
                    4 -> kontrollToggleText(def.aktivWa)
                    5 -> kontrollToggleText(def.aktivWe)
                    6 -> kontrollToggleText(def.kontrollPflicht)
                    else -> value
                }
            }
        },
        editable = true,
        nonEditableCols = setOf("ID"),
        toggleColumns = setOf("WA", "WE", "Pflicht"),
        dropdownOptions = mapOf("Typ" to listOf("checkbox", "text", "dropdown")),
        createRow = { _ ->
            val neueId = ((items.maxOfOrNull { it.kontrollId.toIntOrNull() ?: 0 } ?: 0) + 1).toString()
            if (neueId.isNotBlank()) listOf(neueId, neueId, "", "", "false", "false", "true", "checkbox", "") else
            listOf(neueId, "", "", "⬜", "⬜", "✅", "checkbox", "")
        },
        onCommit = { rows ->
            val updatedItems = rows.map { (id, cols) ->
                val existing = items.firstOrNull { it.kontrollId == id }
                val resolvedPrio = cols.getOrElse(1) { "" }.toIntOrNull()
                    ?: existing?.abfragePrio?.takeIf { it > 0 }
                    ?: id.toIntOrNull()
                    ?: 999
                (existing ?: All(id = id, kontrollId = id)).copy(
                    id = id,
                    kontrollId = id,
                    abfragePrio = resolvedPrio,
                    bezeichnung = cols.getOrElse(2) { "" },
                    kontrollBezeichnung = cols.getOrElse(3) { "" },
                    aktivWa = kontrollToggleValue(cols.getOrElse(4) { "" }),
                    aktivWe = kontrollToggleValue(cols.getOrElse(5) { "" }),
                    kontrollPflicht = kontrollToggleValue(cols.getOrElse(6) { "true" }),
                    bedeutung = "",
                    kontrollTyp = cols.getOrElse(7) { "checkbox" },
                    kontrollOptions = cols.getOrElse(8) { "" }
                        .split(",")
                        .map { it.trim() }
                        .filter { it.isNotBlank() }
                )
            }
            val previousById = items.associateBy { it.kontrollId }
            items = sortKontrollen(updatedItems)
            scope.launch {
                val changedItems = updatedItems.filter { updated ->
                    val existing = previousById[updated.kontrollId]
                    existing != updated
                }
                if (changedItems.isNotEmpty()) {
                    YardApi.saveKontrolleDefinitionen(changedItems)
                }
            }
        },
        onDeleteRows = { ids ->
            GlobalManager.markLocalScreenWrite("KontrollVerwaltung")
            ids.forEach { id -> YardApi.deleteKontrolleDefinition(id) }
            val remainingItems = sortKontrollen(items.filterNot { it.kontrollId in ids })
            if (remainingItems.isEmpty()) {
                val defaults = sortKontrollen(standardKontrollen.map { it.normalizedKontrolle() })
                YardApi.saveKontrolleDefinitionen(defaults)
                items = defaults
            } else {
                items = remainingItems
            }
        }
    )
}

val standardKontrollen: List<All> = listOf(
    All(id = "1", kontrollId = "1", bezeichnung = "temperaturkontrolle", kontrollBezeichnung = "Welche Temperatur wurde im Laderaum gemessen?", aktivWe = true, aktivWa = false, kontrollTyp = "checkbox"),
    All(id = "2", kontrollId = "2", bezeichnung = "schädlingskontrolle", kontrollBezeichnung = "Keine Spuren von Schädlingen im Laderaum", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "3", kontrollId = "3", bezeichnung = "sauberkeit", kontrollBezeichnung = "Visuelle Prüfung auf Verschmutzung / Verunreinigung", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "4", kontrollId = "4", bezeichnung = "ladungGesichert", kontrollBezeichnung = "Paletten gegen Verrutschen / Umfallen gesichert", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "5", kontrollId = "5", bezeichnung = "verpackungBeschädigt", kontrollBezeichnung = "Äußere Verpackung ohne Schäden", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "6", kontrollId = "6", bezeichnung = "fahrerHatHalleBetreten", kontrollBezeichnung = "Fahrer hat die Halle betreten", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "7", kontrollId = "7", bezeichnung = "gefahrengutzulassung", kontrollBezeichnung = "Fahrzeug ist für Gefahrgut zugelassen", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "8", kontrollId = "8", bezeichnung = "gefahrenguttransport", kontrollBezeichnung = "Fahrzeug hat Gefahrgut geladen", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "9", kontrollId = "9", bezeichnung = "gefahrengutabladungok", kontrollBezeichnung = "Gefahrgut wurde ohne Mängel angeliefert", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "10", kontrollId = "10", bezeichnung = "feuchtigkeit", kontrollBezeichnung = "Keine Spuren von Feuchtigkeit im Laderaum", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "11", kontrollId = "11", bezeichnung = "wareweGeprüft", kontrollBezeichnung = "Die Ware wurde unbeschädigt entgegengenommen", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true, kontrollPflicht = false),
    All(id = "12", kontrollId = "12", bezeichnung = "warewaGeprüft", kontrollBezeichnung = "Die Ware wurde vor dem Verladen auf Vollständigkeit geprüft", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "13", kontrollId = "13", bezeichnung = "mhdGeprüft", kontrollBezeichnung = "Mindesthaltbarkeitsdatum geprüft", aktivWe = false, aktivWa = false, kontrollTyp = "checkbox", istCheckbox = true),
    All(id = "14", kontrollId = "14", bezeichnung = "verladeprotokoll", kontrollBezeichnung = "Die Ware wurde mit folgendem Gerät verladen:", aktivWe = false, aktivWa = false, kontrollTyp = "dropdown", kontrollOptions = listOf("Außenstapler", "Hubwagen", "Doppelstock", "Schnellläufer"))
)
